import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ArrowRight, Shield, Zap, FileText, ChevronDown, Lock } from "lucide-react";
import { Button } from "../../components/ui";
import { Link, useSearchParams } from "react-router-dom";
import { device } from "../../lib/breakpoints";
import ProjectedSavingsCalculator from "../../components/home/ProjectedSavingsCalculator";
import IntegrationIngestLogos from "../../components/home/IntegrationIngestLogos";
import { DIALOG_NAME } from "../../helpers/useDialogSearchParam";
import {
  APP_HERO_HEADLINE,
  APP_HERO_PROOF,
  APP_HERO_PROOF_SOURCE,
  PERFORMANCE_FEE_DISCLAIMER,
} from "../../helpers/constants";
import { Helmet } from "react-helmet";
import { trackPageViewOnce, trackPortalCta } from "../../lib/siteActivity";

const FAQ_PAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is a phantom asset?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A phantom asset is equipment that has been scrapped, sold, relocated, or fully depreciated but remains on the county Business Personal Property (BPP) tax roll, so you keep paying tax on assets you no longer own or use.",
      },
    },
    {
      "@type": "Question",
      name: "How does the 35% performance fee work?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "You pay nothing upfront. SolvTax collects 35% only of realized, verified reductions to your BPP tax bill. If we do not lower your assessment, your fee is $0.",
      },
    },
    {
      "@type": "Question",
      name: "How do you analyze my tax notice?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We parse your county BPP assessment notice and cross-reference every machinery and equipment line against your asset ledger and statutory depreciation schedules, including assessment ratios such as Missouri’s 33.33% of historical cost to identify over-assessment variances and phantom assets.",
      },
    },
  ],
};

const SYSTEM_PILLARS = [
  {
    title: "County Notice Processing",
    body: "AI parses your county Business Personal Property (BPP) assessment notice and flags over-assessed machinery, equipment, and furniture lines against your fixed-asset ledger.",
  },
  {
    title: "Depreciation Schedule Corrections",
    body: "Cross-references statutory assessment ratios, useful-life tables, and percent-good schedules, including Missouri’s standard 33.33% assessment ratio against historical cost to compute defensible assessed value.",
  },
  {
    title: "Audit-Ready Appeal Packets",
    body: "Compiles your BPP appeal, corrected depreciation schedule, and evidence cover packet for county review without manual handoffs or real-estate filings.",
  },
  {
    title: "35% Performance Fee",
    body: "We collect 35% only on realized, verified tax-bill reductions. No upfront retainers. If over-assessment variances are not corrected, you pay\u00A0$0.",
  },
];

const TIMELINE_STEPS = [
  {
    step: "01",
    title: "Secure Account Setup",
    duration: "60 Seconds",
    body: "Create a secure corporate portal account, then upload your county BPP notice and equipment asset ledger inside your encrypted ledger room. Diagnostic completes in under 60 seconds.",
  },
  {
    step: "02",
    title: "AI BPP Audit",
    duration: "45 Seconds",
    body: "Our engine cross-references every machinery and equipment line against state depreciation schedules and percent-good tables to surface phantom assets and misclassifications.",
  },
  {
    step: "03",
    title: "Review Savings",
    duration: "15 Seconds",
    body: "Review projected BPP tax savings from depreciation schedule corrections. When discrepancies are found, we compile an audit-ready appeal package for filing.",
  },
];

const ENGINE_LOG_LINES = [
  {
    tag: "UPLOAD",
    message:
      "Successfully processed 412 corporate equipment line items (machinery, tools, server arrays).",
    tone: "text-slate-300",
  },
  {
    tag: "MATCH",
    message:
      "Applying Missouri statutory 33.33% assessment ratio to historical cost under RSMo §137.122.",
    tone: "text-cyan-300",
  },
  {
    tag: "PHANTOM ASSET",
    message:
      "Scrapped CNC line still listed on county roll; flagged for removal from assessed base.",
    tone: "text-amber-300",
  },
  {
    tag: "VALUE CAPTURED",
    message:
      "Realized Tax Bill Reduction: $3,850 (35% performance fee applies only to verified savings).",
    tone: "text-emerald-400",
  },
];

export default function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  useEffect(() => {
    trackPageViewOnce("/", "Home");
  }, []);

  useEffect(() => {
    if (searchParams.get("triggerSignup") !== "true") return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("triggerSignup");
        next.set(DIALOG_NAME.waitlist, "");
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  const faqItems = [
    {
      question: "What is a phantom asset?",
      answer:
        "A phantom asset is machinery or equipment that has been scrapped, sold, relocated, or fully depreciated, but is still listed on your county Business Personal Property (BPP) tax roll. Counties often assess historical cost without reconciling disposals, so finance teams keep paying tax on assets that no longer exist in the business.",
    },
    {
      question: "How does the 35% performance fee work?",
      answer:
        "SolvTax is risk-free for operators. There is no upfront retainer. We collect a 35% performance fee only on realized, verified reductions to your BPP tax bill. If depreciation schedule corrections and phantom-asset removals do not lower what you owe, you pay $0.",
    },
    {
      question: "How do you analyze my tax notice?",
      answer:
        "We ingest your county BPP assessment notice and equipment asset ledger, then cross-reference each line against statutory depreciation schedules and assessment ratios (for example, Missouri’s standard 33.33% assessment ratio applied to historical cost). The engine flags over-assessment variances, misclassified useful lives, and phantom assets, then compiles an audit-ready appeal packet.",
    },
    {
      question: "How does SolvTax reduce Business Personal Property tax?",
      answer:
        "We specialize in BPP only: corporate machinery, precision tools, commercial furniture, and server arrays, not residential or commercial real estate. When we find defensible overcharges, we compile and support your county appeal. No income tax, sales tax, or building valuation work.",
    },
    {
      question: "What documents do I need for the 60-second diagnostic?",
      answer:
        "Two files: your county Business Personal Property assessment notice (PDF) and your equipment asset ledger or depreciation schedule (PDF or CSV). Upload both safely inside your encrypted SolvTax Portal after secure corporate registration.",
    },
    {
      question: "Which industries see the strongest results?",
      answer:
        "Asset-heavy operators: manufacturers, medical practices, restaurants, and other businesses with significant depreciable equipment typically see meaningful assessment reductions when counties apply blanket valuation methods instead of schedule-accurate depreciation.",
    },
    {
      question: "How is my business property data secured?",
      answer:
        "All corporate tax filings and asset depreciation ledgers are managed within your encrypted, password-protected SolvTax Portal. We require corporate identity validation to protect sensitive financial records.",
    },
  ];

  return (
    <Page>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_PAGE_SCHEMA)}</script>
      </Helmet>
      <Hero>
        <HeroInner>
          <HeroCopy className="w-full flex flex-col text-center md:text-left items-center md:items-start">
            <Eyebrow className="text-center md:text-left">
              Business Personal Property (BPP) Tax Optimization
            </Eyebrow>
            <HeroTitle className="text-balance w-full text-center md:text-left">
              {APP_HERO_HEADLINE}
            </HeroTitle>
            <HeroProof className="text-center md:text-left" title={APP_HERO_PROOF_SOURCE}>
              {APP_HERO_PROOF}
            </HeroProof>
            <HeroSub className="text-center md:text-left">
              SolvTax finds savings on your county Business Personal Property (BPP) tax by auditing
              your notice against your equipment ledger, then flagging phantom assets, bad
              depreciation schedules, and other over-assessments on machinery, tools, furniture, and
              servers, not real estate. We compile the appeal packet so verified reductions stick.{" "}
              <HeroFeeMobile>{PERFORMANCE_FEE_DISCLAIMER.replace(/\n/g, " ")}</HeroFeeMobile>
            </HeroSub>
            <HeroDisclaimer className="text-left">{PERFORMANCE_FEE_DISCLAIMER}</HeroDisclaimer>
            <HeroActions>
              <HeroCtaLink to="/?triggerSignup=true" onClick={() => trackPortalCta("home-hero")}>
                <PrimaryCta type="button">
                  <span>Request Access</span>
                  <ArrowRight size={18} className="shrink-0" aria-hidden />
                </PrimaryCta>
              </HeroCtaLink>
              <HeroCtaLink to="/pricing">
                <SecondaryCta type="button">
                  <span>35% Performance Fee</span>
                </SecondaryCta>
              </HeroCtaLink>
            </HeroActions>
            <IntegrationIngestLogos />
          </HeroCopy>

          <HeroCalculator>
            <ProjectedSavingsCalculator />
          </HeroCalculator>
        </HeroInner>
      </Hero>

      <Section $dark $tightBottom id="how-it-works" className="scroll-mt-24">
        <Container>
          <TimelineHeader>
            <SectionLabel>3 Steps · 2 Minutes</SectionLabel>
            <SectionTitle>
              From 60-second upload to BPP savings review, built for CFOs and controllers.
            </SectionTitle>
            <TimelineTotal>Total operator time: under 2 minutes</TimelineTotal>
          </TimelineHeader>
          <TimelineGrid>
            {TIMELINE_STEPS.map((item) => (
              <TimelineCard key={item.title}>
                <TimelineMeta>
                  <TimelineStepNum>{item.step}</TimelineStepNum>
                  <TimelineDuration>{item.duration}</TimelineDuration>
                </TimelineMeta>
                <TimelineHeading>{item.title}</TimelineHeading>
                <TimelineBody>{item.body}</TimelineBody>
              </TimelineCard>
            ))}
          </TimelineGrid>
        </Container>
      </Section>

      <Section $dark $compact style={{ borderTop: 0 }}>
        <Container>
          <div className="max-w-4xl mx-auto bg-slate-950 p-6 rounded-lg border border-slate-800 text-left font-mono text-sm">
            {ENGINE_LOG_LINES.map((line) => (
              <p key={line.tag} className={`mb-3 last:mb-0 ${line.tone}`}>
                <span className="text-slate-500">[{line.tag}]</span> {line.message}
              </p>
            ))}
          </div>
        </Container>
      </Section>

      <Section $dark $looseTop>
        <ArchitectureContainer>
          <ArchitectureHeader>
            <SectionLabel>System Architecture</SectionLabel>
            <ArchitectureTitle>
              Compliance infrastructure for Business Personal Property tax optimization
            </ArchitectureTitle>
          </ArchitectureHeader>
          <PillarGrid>
            {SYSTEM_PILLARS.map((pillar) => (
              <PillarCard key={pillar.title}>
                <PillarTitle>{pillar.title}</PillarTitle>
                <PillarBody>{pillar.body}</PillarBody>
              </PillarCard>
            ))}
          </PillarGrid>
        </ArchitectureContainer>
      </Section>

      <Section>
        <Container>
          <ProcessRow>
            <ProcessBlock>
              <ProcessStep>01</ProcessStep>
              <ProcessTitle>Submit Two Documents</ProcessTitle>
              <ProcessText>
                County BPP assessment notice + internal equipment asset ledger. We validate file
                types and secure documents for depreciation schedule analysis, not real-property
                appraisals.
              </ProcessText>
            </ProcessBlock>
            <ProcessBlock>
              <ProcessStep>02</ProcessStep>
              <ProcessTitle>Engine Audits & Compiles</ProcessTitle>
              <ProcessText>
                Extraction, statutory ratio checks, phantom-asset detection, and evidence packet
                assembly, fully automated for audit-ready compliance.
              </ProcessText>
            </ProcessBlock>
            <ProcessBlock>
              <ProcessStep>03</ProcessStep>
              <ProcessTitle>Appeal Filed. Pay Only on Savings.</ProcessTitle>
              <ProcessText>
                When assessed value drops, you keep 65% of the verified tax savings. We collect a
                35% performance fee. Zero reduction means zero fee.
              </ProcessText>
            </ProcessBlock>
          </ProcessRow>
        </Container>
      </Section>

      <TrustBar>
        <Container>
          <TrustItems className="justify-center mx-auto items-center text-center">
            <TrustItem>
              <Lock className="h-5 w-5" />
              <span>Documents uploaded securely</span>
            </TrustItem>
            <TrustItem>
              <Shield className="h-5 w-5" />
              <span>35% fee only on verified BPP savings</span>
            </TrustItem>
            <TrustItem>
              <Zap className="h-5 w-5" />
              <span>Statutory depreciation schedule engine</span>
            </TrustItem>
            <TrustItem>
              <FileText className="h-5 w-5" />
              <span>Audit-ready appeal evidence</span>
            </TrustItem>
          </TrustItems>
        </Container>
      </TrustBar>

      <Section $muted>
        <Container>
          <SectionLabel>FAQ</SectionLabel>
          <SectionTitle>Direct answers for CFOs, controllers, and operators</SectionTitle>
          <FaqList>
            {faqItems.map((item, index) => (
              <FaqItem key={item.question} $open={openFaqIndex === index}>
                <FaqButton
                  type="button"
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  aria-expanded={openFaqIndex === index}
                >
                  {item.question}
                  <FaqChevron $open={openFaqIndex === index}>
                    <ChevronDown className="h-5 w-5" />
                  </FaqChevron>
                </FaqButton>
                <FaqAnswer $open={openFaqIndex === index}>
                  <p className="text-left text-slate-600 dark:text-slate-400 text-sm leading-relaxed pt-2 pb-4 px-1">
                    {item.answer}
                  </p>
                </FaqAnswer>
              </FaqItem>
            ))}
          </FaqList>
        </Container>
      </Section>

      <FinalCta>
        <Container>
          <FinalTitle>Ready to save on your business equipment tax bill?</FinalTitle>
          <FinalSub>{PERFORMANCE_FEE_DISCLAIMER}</FinalSub>
          <Link to="/?triggerSignup=true" onClick={() => trackPortalCta("home-final-cta")}>
            <FinalButton size="xl">
              Request Access
              <ArrowRight className="h-5 w-5" />
            </FinalButton>
          </Link>
        </Container>
      </FinalCta>
    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  background: #0f172a;
  color: #e2e8f0;
`;

const Hero = styled.section`
  border-bottom: 1px solid #1e293b;
  background: #0f172a;
`;

const HeroInner = styled.div`
  max-width: 80rem;
  margin: 0 auto;
  padding: 72px 1rem 96px;
  display: grid;
  gap: 56px;
  align-items: start;

  @media (min-width: 640px) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  @media (min-width: 768px) {
    padding-left: 3rem;
    padding-right: 3rem;
  }

  @media (min-width: 1024px) {
    grid-template-columns: 55% 45%;
    align-items: center;
    padding-top: 96px;
    padding-bottom: 112px;
    gap: 72px;
  }
`;

const HeroCopy = styled.div`
  max-width: 36rem;
  width: 100%;

  @media (min-width: 768px) {
    margin-right: auto;
  }
`;

const HeroCalculator = styled.div`
  width: 100%;
`;

const Eyebrow = styled.p`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #22d3ee;
  margin: 0 0 20px;
`;

const HeroTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  line-height: 1.2;
  color: #f8fafc;
  margin: 0 0 16px;
  letter-spacing: -0.02em;
  text-wrap: balance;

  @media (min-width: 640px) {
    font-size: 2.75rem;
    line-height: 1.15;
    letter-spacing: -0.03em;
  }

  @media (min-width: 1024px) {
    font-size: 3.25rem;
    line-height: 1.12;
    letter-spacing: -0.035em;
  }
`;

const HeroProof = styled.p`
  font-size: 0.9375rem;
  font-weight: 500;
  line-height: 1.5;
  color: #67e8f9;
  margin: 0 0 20px;
  max-width: 48ch;

  @media (min-width: 1024px) {
    font-size: 1rem;
    margin-bottom: 24px;
  }
`;

const HeroSub = styled.p`
  font-size: 1.0625rem;
  line-height: 1.7;
  color: #94a3b8;
  margin: 0 0 24px;
  max-width: 56ch;

  @media (min-width: 768px) {
    margin-bottom: 12px;
  }

  @media (min-width: 1024px) {
    font-size: 1.125rem;
    max-width: 60ch;
  }
`;

const HeroFeeMobile = styled.span`
  @media (min-width: 768px) {
    display: none;
  }
`;

const HeroDisclaimer = styled.p`
  display: none;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #64748b;
  margin: 0 0 24px;
  max-width: 56ch;
  white-space: pre-line;

  @media (min-width: 768px) {
    display: block;
  }

  @media (min-width: 1024px) {
    margin-bottom: 28px;
    max-width: 60ch;
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1rem;
  width: 100%;
  max-width: 32rem;
  margin-top: 2rem;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: stretch;
  }

  @media (min-width: 768px) {
    margin-left: 0;
    margin-right: auto;
  }
`;

const HeroCtaLink = styled(Link)`
  flex: 1 1 0;
  display: flex;
  min-width: 0;
  text-decoration: none;
`;

const heroCtaShared = `
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  min-height: 58px;
  padding: 1rem 1.5rem;
  border-radius: 0;
  font-family: inherit;
  font-size: 0.875rem;
  letter-spacing: normal;
  text-transform: none;
  white-space: nowrap;
  line-height: 1.25;
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease,
    box-shadow 150ms ease;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const PrimaryCta = styled.button`
  ${heroCtaShared}
  background: #22d3ee;
  color: #0f172a;
  border: 1px solid #22d3ee;
  font-weight: 700;
  box-shadow: 0 10px 15px -3px rgba(34, 211, 238, 0.1);

  &:hover {
    background: #06b6d4;
    border-color: #06b6d4;
    color: #0f172a;
  }

  &:focus-visible {
    outline: 2px solid rgba(34, 211, 238, 0.5);
    outline-offset: 2px;
  }
`;

const SecondaryCta = styled.button`
  ${heroCtaShared}
  background: rgba(11, 15, 25, 0.4);
  color: #e2e8f0;
  border: 1px solid #1e293b;
  font-weight: 600;

  &:hover {
    background: #1e293b;
    border-color: #334155;
    color: #f8fafc;
  }

  &:focus-visible {
    outline: 2px solid rgba(148, 163, 184, 0.5);
    outline-offset: 2px;
  }
`;

const Section = styled.section`
  padding: ${(p) => {
    if (p.$compact) return "0 0 80px";
    if (p.$looseTop) return "80px 0 96px";
    if (p.$dark && p.$tightBottom) return "96px 0 48px";
    if (p.$dark) return "96px 0";
    if (p.$muted) return "80px 0";
    return "80px 0";
  }};
  background: ${(p) => (p.$dark ? "#0f172a" : p.$muted ? "#f8fafc" : "#ffffff")};
  color: ${(p) => (p.$dark ? "#e2e8f0" : "#0f172a")};
  border-top: ${(p) => (p.$dark ? "1px solid #1e293b" : "none")};
`;

const TimelineHeader = styled.div`
  margin-bottom: 40px;

  @media (min-width: 1024px) {
    margin-bottom: 48px;
  }
`;

const TimelineTotal = styled.p`
  margin: 12px 0 0;
  font-size: 0.9375rem;
  color: #94a3b8;
`;

const TimelineGrid = styled.div`
  display: grid;
  gap: 20px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
`;

const TimelineCard = styled.div`
  border: 1px solid #1e293b;
  background: rgba(11, 18, 32, 0.65);
  padding: 28px 24px;
`;

const TimelineMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const TimelineStepNum = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: #22d3ee;
`;

const TimelineDuration = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #64748b;
`;

const TimelineHeading = styled.h3`
  margin: 0 0 12px;
  font-size: 1.125rem;
  font-weight: 600;
  color: #f8fafc;
  letter-spacing: -0.01em;
`;

const TimelineBody = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.65;
  color: #94a3b8;
`;

const EngineHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;

  @media (min-width: 1024px) {
    margin-bottom: 40px;
  }
`;

const ArchitectureContainer = styled.div`
  max-width: 80rem;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;
  box-sizing: border-box;

  @media (${device.tablet}) {
    padding: 0 3rem;
  }
`;

const ArchitectureHeader = styled.div`
  margin-bottom: 48px;

  @media (min-width: 1024px) {
    margin-bottom: 56px;
  }
`;

const Container = styled.div`
  max-width: 80rem;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;
  box-sizing: border-box;

  @media (${device.tablet}) {
    padding: 0 3rem;
  }
`;

const SectionLabel = styled.p`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #22d3ee;
  margin: 0 0 12px;
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 40px;
  letter-spacing: -0.025em;
  line-height: 1.2;
  color: inherit;

  @media (min-width: 1024px) {
    font-size: 2rem;
  }
`;

const EngineTitle = styled(SectionTitle)`
  margin-bottom: 0;
`;

const ArchitectureTitle = styled(SectionTitle)`
  margin-bottom: 0;
`;

const PillarGrid = styled.div`
  display: grid;
  gap: 1px;
  background: rgba(51, 65, 85, 0.6);
  border: 1px solid rgba(30, 41, 59, 0.8);

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PillarCard = styled.div`
  background: rgba(15, 23, 42, 0.55);
  padding: 36px 28px;

  @media (min-width: 1024px) {
    padding: 40px 32px;
  }
`;

const PillarTitle = styled.h3`
  margin: 0 0 14px;
  font-size: 1.0625rem;
  font-weight: 600;
  color: #f8fafc;
  letter-spacing: -0.01em;
`;

const PillarBody = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.7;
  color: #94a3b8;
`;

const ProcessRow = styled.div`
  display: grid;
  gap: 32px;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
`;

const ProcessBlock = styled.div`
  border: 1px solid #e2e8f0;
  padding: 28px 24px;
`;

const ProcessStep = styled.span`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #22d3ee;
  margin-bottom: 12px;
`;

const ProcessTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 1.0625rem;
  font-weight: 600;
  color: #0f172a;
`;

const ProcessText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #475569;
`;

const TrustBar = styled.section`
  background: #0b1220;
  border-top: 1px solid #1e293b;
  border-bottom: 1px solid #1e293b;
  padding: 28px 0;
`;

const TrustItems = styled.div`
  display: grid;
  gap: 16px;
  justify-items: center;
  max-width: 56rem;
  margin: 0 auto;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 0.8125rem;
  color: #94a3b8;
  text-align: center;

  svg {
    color: #22d3ee;
    flex-shrink: 0;
  }
`;

const FaqList = styled.div`
  width: 100%;
  max-width: 48rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FaqItem = styled.div`
  border: 1px solid ${(p) => (p.$open ? "#22d3ee" : "#e2e8f0")};
  background: white;
`;

const FaqButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  text-align: left;
  padding: 18px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  color: #0f172a;
`;

const FaqChevron = styled.span`
  flex-shrink: 0;
  transition: transform 0.2s;
  transform: ${(p) => (p.$open ? "rotate(180deg)" : "none")};
  color: #64748b;
`;

const FaqAnswer = styled.div`
  overflow: hidden;
  max-height: ${(p) => (p.$open ? "400px" : "0")};
  transition: max-height 0.25s ease;
  text-align: left;

  p {
    margin: 0;
    padding: 0 20px 18px;
    font-size: 0.9375rem;
    line-height: 1.6;
    color: #475569;
    text-align: left;
  }
`;

const FinalCta = styled.section`
  padding: 80px 0;
  background: #0f172a;
  border-top: 1px solid #334155;
  text-align: center;
`;

const FinalTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #f8fafc;
  margin: 0 0 12px;
  letter-spacing: -0.02em;
`;

const FinalSub = styled.p`
  font-size: 1rem;
  color: #94a3b8;
  margin: 0 0 28px;
  white-space: pre-line;
`;

const FinalButton = styled(Button)`
  background: #22d3ee !important;
  color: #0b1220 !important;
  border: none !important;
  font-weight: 700 !important;
  border-radius: 0 !important;

  &:hover {
    background: #67e8f9 !important;
  }
`;
